import {inject, Injectable, signal} from '@angular/core';
import {AppLocalStorage} from '../lib/app-localstorage';
import {AppComponent} from '../app.component';
import {ApiEnvironment, AppConstants} from '../app.constants';
import {ApiConfiguration} from '../restclient/api.config';
import {AppRoleAndPermissions, AppRolePermissionHeader} from '../security/app-role-and-permissions';
import {AccessControlService} from '../security/access-control.service';


const DEVTOOLS_API_USER_ROLE_KEY = AppConstants.DEVTOOLS_API_USER_ROLE_KEY;

@Injectable({
    providedIn: 'root'
})
export class AppDevToolsService {

    readonly isDevMode = signal(this.readDevMode());

    // Role permissions as a signal, intended for the main-layout to display that
    // the developer mode is active with specific role permissions
    private readonly _rolePermissions = signal(this.readStoredRolePermissions());
    readonly rolePermissions = this._rolePermissions.asReadonly();

    private devKeyCombinationCount = 0;

    private readonly accessControlService = inject(AccessControlService);

    constructor() {
        window.addEventListener('keydown', this.onKeyDown.bind(this));
        const rolePermissions = this.readStoredRolePermissionsAsObject();
        if (rolePermissions) {
            this.applyRolePermissions(rolePermissions);
        }
        this.initApiEnvironments();
    }

    setDevMode(active: boolean) {
        AppLocalStorage.setItem(AppConstants.DEVTOOLS_ENABLED_LOCAL_STORAGE_KEY, active);
        if (!active) this.removeAllDevToolsOptions()
        this.isDevMode.set(active);
    }

    removeAllDevToolsOptions() {
        AppConstants.clearDevMode();
        this.applyRolePermissions(null);
        this.applyApiEnvKt(null);
        this.applyApiEnvPy(null);
    }

    private readDevMode() {
        return AppLocalStorage.getBooleanItem(AppConstants.DEVTOOLS_ENABLED_LOCAL_STORAGE_KEY);
    }

    protected onKeyDown(event: KeyboardEvent) {
        if (event.key === 'q' && event.ctrlKey) {
            const keyPressesInArow = ++this.devKeyCombinationCount;
            if (keyPressesInArow >= 5) {
                this.devKeyCombinationCount = 0;
                this.setDevMode(!this.isDevMode());
                const action = this.isDevMode() ? 'activated' : 'deactivated';
                AppComponent.messageToast.show({ message: `Developer mode ${action}` });
            } else if (keyPressesInArow === 3) {
                AppComponent.messageToast.show({ message: '2 more key presses toggles your developer status' });
            }
        }
    }

    private initApiEnvironments() {
        const envKt = AppConstants.getApiEnvKt();
        const envPy = AppConstants.getApiEnvPy();
        if (envKt) ApiConfiguration.setApiEnvKt(envKt);
        if (envPy) ApiConfiguration.setApiEnvPy(envPy);
    }

    applyApiEnvPy(envApiPy: ApiEnvironment) {
        AppConstants.setApiEnvPy(envApiPy);
        ApiConfiguration.setApiEnvPy(envApiPy);
    }

    applyApiEnvKt(envApiKt: ApiEnvironment) {
        AppConstants.setApiEnvKt(envApiKt);
        ApiConfiguration.setApiEnvKt(envApiKt);
    }

    private readStoredRolePermissions(): AppRolePermissionHeader | null {
        return AppLocalStorage.getJsonItem(DEVTOOLS_API_USER_ROLE_KEY);
    }

    private readStoredRolePermissionsAsObject(): AppRoleAndPermissions | null {
        const rolePermissionsHeader = this.readStoredRolePermissions();
        if (rolePermissionsHeader) {
            return AppRoleAndPermissions.fromHeaderValue(rolePermissionsHeader);
        }
        return null;
    }

    applyRolePermissions(rolePermissions: AppRoleAndPermissions | null) {
        const rolePermissionsHeader = rolePermissions?.toHeaderValue();
        this._rolePermissions.set(rolePermissionsHeader ?? null);
        this.accessControlService.applyRolePermissions(rolePermissions);
        ApiConfiguration.setDevToolsRoleHeader(JSON.stringify(rolePermissionsHeader));
        AppLocalStorage.setJsonItem(DEVTOOLS_API_USER_ROLE_KEY, rolePermissionsHeader);
    }
}
